import type { ComponentProps } from 'svelte'
import type { AppContext } from 'types/common'
export type AsrTabs = ComponentProps<typeof AsrNavTabBar>['tabs']

import component from './Configuration.svelte'
import { checkSessionPermission, hasPermission } from 'utility/permission'
import { AsrNavTabBar } from '@isoftdata/svelte-nav-tab-bar'
import { tr } from 'date-fns/locale'

interface User {
	settings: {
		preferences: {
			optInToTestingFeatures: boolean
		}
	}
}

export default function ({ stateRouter, i18next: { t: translate } }: AppContext) {
	stateRouter.addState({
		name: 'app.configuration',
		route: 'configuration',
		//@ts-expect-error the type is wrong but this is valid
		defaultChild: () => {
			const childStates = getChildStates(translate)
			const activeChildState = childStates.find(childState => !childState.hidden)
			return activeChildState?.name ? activeChildState.name.replace('app.configuration.', '') : undefined // return just the name of the child state
		},
		template: {
			component,
			svelte: true,
		},
		resolve(_data, _parameters) {
			const childStates = getChildStates(translate)
			return Promise.resolve({
				childStates,
				hasVisibleChildState: childStates.some(childState => !childState.hidden),
			})
		},
	})
}

function getChildStates(translate: AppContext['i18next']['t']): AsrTabs {
	const user = JSON.parse(sessionStorage.getItem('user') ?? '{}') as User
	return [
		{
			icon: 'chart-line',
			title: translate('common:dashboard', 'Dashboard'),
			name: 'app.configuration.dashboard',
			parameters: {},
			hidden:
				!checkSessionPermission('DASHBOARD_REPORT_EDIT_ALL') && !checkSessionPermission('DASHBOARD_REPORT_EDIT_OWN'),
		},
		{
			icon: 'users',
			title: translate('common:groups', 'Groups'),
			name: 'app.configuration.group',
			parameters: {},
			// Global required
			hidden: !hasPermission('SETTINGS_GROUP_ADMINISTRATOR', -1),
		},
		{
			icon: 'engine',
			title: translate('common:inventoryTypes', 'Inventory Types'),
			name: 'app.configuration.inventory-type',
			parameters: {},
			hidden: !checkSessionPermission('PART_CONFIGURATION_CAN_EDIT_PART_TYPES'),
		},
		{
			icon: 'address-card',
			title: translate('common:myAccount', 'My Account'),
			name: 'app.configuration.my-account',
		},
		{
			icon: 'barcode-alt',
			title: translate('common:scanner', 'Scanner'),
			name: 'app.configuration.scanner',
			parameters: {},
		},
		{
			icon: 'sliders',
			title: translate('common:settings', 'Settings'),
			name: 'app.configuration.settings',
			parameters: {},
			hidden: false, // All users have permission to edit their own settings the Screen itself needs to handle hiding global and Store settings
		},
		{
			icon: 'store-alt',
			title: translate('common:store', 'Store'),
			name: 'app.configuration.store',
			parameters: {},
			hidden:
				!user.settings.preferences.optInToTestingFeatures &&
				!checkSessionPermission('CONFIGURATION_CAN_CONFIGURE_STORES'),
		},
		{
			icon: 'user',
			title: translate('common:user', 'User'),
			name: 'app.configuration.user',
			parameters: {},
			// Global required
			hidden: !checkSessionPermission('GLOBAL_USER_ADMINISTRATOR'),
		},
	]
}
